/**
 * MonthlyCategoryChart — professional single-colour category bar chart.
 *
 * X-axis  : categories (sorted descending by amount)
 * Y-axis  : $ amount for the selected month (or "All time" if none)
 * Bars    : one uniform brand-navy colour, value label on top of each bar
 * Filter  : month pill row at the top; clicking a month narrows the chart,
 *           "All time" restores the aggregate view.
 *
 * Designed to feel like an equity-research/finance dashboard: tabular
 * numbers, sparse chrome, subtle grid, no rainbow palette.
 */
import { useMemo } from "react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, LabelList, Cell,
  ResponsiveContainer,
} from "recharts";
import { money } from "@/lib/operations/api";

// Uniform bar colour — HepCURE brand-navy. One accent used for the
// currently-hovered / top bar so the chart doesn't look flat.
const BAR_COLOR       = "#0f2c4f";

const CHART_HEIGHT   = 380;
const CATEGORY_SLOT_PX = 92; // horizontal room reserved per category bar

// "YYYY-MM" → "Jun 2026"
function longMonth(ym) {
  const [y, m] = ym.split("-");
  const d = new Date(Number(y), Number(m) - 1, 1);
  return d.toLocaleString("en-US", { month: "short", year: "numeric" });
}

// Money for axis / labels — compact ("$68k") for readability.
function moneyCompact(v) {
  const n = Number(v) || 0;
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000)     return `$${(n / 1_000).toFixed(1)}k`;
  return `$${Math.round(n)}`;
}

export default function MonthlyCategoryChart({
  months, byCategoryMonthly, categories,
  selectedMonth, onSelectMonth,
  onBarClick,          // (category_key) => void
}) {  // Rows: one per category, amount = sum across selected month(s).
  const rows = useMemo(() => {
    return categories
      .map((c) => {
        const perMonth = byCategoryMonthly?.[c.key] || {};
        const amount =
          selectedMonth === "all"
            ? Object.values(perMonth).reduce((s, v) => s + Number(v || 0), 0)
            : Number(perMonth[selectedMonth] || 0);
        return { key: c.key, label: c.label, amount };
      })
      .filter((r) => r.amount > 0)
      .sort((a, b) => b.amount - a.amount);
  }, [categories, byCategoryMonthly, selectedMonth]);

  const grandTotal = rows.reduce((s, r) => s + r.amount, 0);

  return (
    <div
      className="rounded-2xl bg-white border border-slate-200"
      data-testid="ops-monthly-chart"
    >
      {/* Header + month filter */}
      <div className="px-5 sm:px-6 pt-5 pb-4 border-b border-slate-100">
        <div className="flex items-baseline justify-between flex-wrap gap-2">
          <div>
            <h3 className="text-lg font-bold text-brand-navy tracking-tight">
              Spend by category
            </h3>
            <p className="text-xs text-slate-500 mt-0.5 tabular-nums">
              {selectedMonth === "all" ? "All months" : longMonth(selectedMonth)}
              {" · "}
              <span className="font-semibold text-slate-700">{money(grandTotal)}</span>
            </p>
          </div>
        </div>

        {/* Month pill row — horizontally scrollable if many months */}
        <div className="mt-4 -mx-1 overflow-x-auto">
          <div className="inline-flex items-center gap-1.5 px-1 pb-1">
            <MonthPill
              active={selectedMonth === "all"}
              onClick={() => onSelectMonth("all")}
              testid="ops-month-all"
            >
              All time
            </MonthPill>
            {months.map((m) => (
              <MonthPill
                key={m}
                active={selectedMonth === m}
                onClick={() => onSelectMonth(m)}
                testid={`ops-month-${m}`}
              >
                {longMonth(m)}
              </MonthPill>
            ))}
          </div>
        </div>
      </div>

      {/* Chart */}
      <div className="p-4 sm:p-6">
        {rows.length === 0 ? (
          <div className="h-40 flex items-center justify-center text-sm text-slate-400">
            No spend recorded for {selectedMonth === "all" ? "any month" : longMonth(selectedMonth)}.
          </div>
        ) : (
          <div className="overflow-x-auto -mx-1">
            <div
              style={{
                width: Math.max(560, rows.length * CATEGORY_SLOT_PX + 96),
                height: CHART_HEIGHT,
              }}
              className="px-1"
            >
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={rows}
                  margin={{ top: 24, right: 16, left: 0, bottom: 8 }}
                >
                  <CartesianGrid stroke="#f1f5f9" vertical={false} />
                  <XAxis
                    dataKey="label"
                    tick={{ fontSize: 12, fill: "#334155", fontWeight: 600 }}
                    axisLine={{ stroke: "#e2e8f0" }}
                    tickLine={false}
                    interval={0}
                    height={44}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: "#94a3b8" }}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={moneyCompact}
                    width={56}
                  />
                  <Tooltip
                    cursor={{ fill: "rgba(15,44,79,0.04)" }}
                    contentStyle={{
                      borderRadius: 6,
                      border: "1px solid #e2e8f0",
                      fontSize: 12,
                      padding: "8px 12px",
                      boxShadow: "0 4px 12px rgba(15,23,42,0.06)",
                    }}
                    formatter={(v) => [money(Number(v)), "Amount"]}
                    labelStyle={{ color: "#0f172a", fontWeight: 700, marginBottom: 4 }}
                  />
                  <Bar
                  dataKey="amount"
                  radius={[3, 3, 0, 0]}
                  maxBarSize={48}
                  cursor="pointer"
                  onClick={(d) => onBarClick?.(d?.key)}
                >
                    {rows.map((r) => (
                      <Cell key={r.key} fill={BAR_COLOR} />
                    ))}
                    <LabelList
                      dataKey="amount"
                      position="top"
                      formatter={moneyCompact}
                      style={{ fill: "#0f2c4f", fontSize: 11, fontWeight: 700 }}
                    />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function MonthPill({ active, onClick, children, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`shrink-0 h-8 px-3.5 text-xs font-semibold rounded-full transition tabular-nums whitespace-nowrap ${
        active
          ? "bg-brand-navy text-white shadow-sm"
          : "text-slate-600 hover:text-brand-navy hover:bg-slate-100"
      }`}
    >
      {children}
    </button>
  );
}
